import { T, useGT } from 'gt-react';
import {
  CheckCircleIcon,
  SpinnerGapIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import {
  CorrectionStrategy,
  ValidationRuleStatus,
  ValidationRuleTrigger,
  ValidationRuleType,
  ValidationRunStatus,
} from '@erp/shared-types';
import { Badge } from '@/components/ui/badge';

/**
 * How a validation rule, its runs and its labels are rendered — shared by the
 * rules list and one rule's detail page so the same status never reads two
 * different ways in the two places.
 */

/** An ISO timestamp as the local date and time, or verbatim if unparseable. */
export function formatValidationTimestamp(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

/**
 * A scheduled fire time in the reader's own timezone.
 *
 * The cron itself is written in UTC, but "when will this happen" is a question
 * about the reader's day, so the answer is local — the same choice
 * `formatValidationTimestamp` already makes for run history.
 */
export function formatScheduleTime(date: Date): string {
  return date.toLocaleString();
}

export function RuleStatusBadge({ status }: { status: ValidationRuleStatus }) {
  switch (status) {
    case ValidationRuleStatus.Active:
      return (
        <Badge variant="success">
          <T context="validation rule status badge">Active</T>
        </Badge>
      );
    case ValidationRuleStatus.Paused:
      return (
        <Badge variant="warning">
          <T context="validation rule status badge">Paused</T>
        </Badge>
      );
    case ValidationRuleStatus.Draft:
      return (
        <Badge variant="secondary">
          <T context="validation rule status badge">Draft</T>
        </Badge>
      );
  }
}

/** How one run ended, as an icon with its outcome as the accessible name. */
export function RunStatusIcon({ status }: { status: ValidationRunStatus }) {
  const gt = useGT();

  switch (status) {
    case ValidationRunStatus.Succeeded:
      return (
        <CheckCircleIcon
          className="h-4 w-4 shrink-0 text-green-600"
          aria-label={gt('Succeeded', {
            context: 'outcome of the last validation run',
          })}
        />
      );
    case ValidationRunStatus.Failed:
      return (
        <WarningCircleIcon
          className="h-4 w-4 shrink-0 text-red-600"
          aria-label={gt('Failed', {
            context: 'outcome of the last validation run',
          })}
        />
      );
    case ValidationRunStatus.Running:
      return (
        <SpinnerGapIcon
          className="h-4 w-4 shrink-0 animate-spin text-muted-foreground"
          aria-label={gt('Running', {
            context: 'outcome of the last validation run — still in progress',
          })}
        />
      );
  }
}

/** How one run ended, as a badge — the run-history table's outcome column. */
export function RunOutcomeBadge({ status }: { status: ValidationRunStatus }) {
  switch (status) {
    case ValidationRunStatus.Succeeded:
      return (
        <Badge variant="success">
          <T context="validation run outcome badge">Succeeded</T>
        </Badge>
      );
    case ValidationRunStatus.Failed:
      return (
        <Badge variant="destructive">
          <T context="validation run outcome badge">Failed</T>
        </Badge>
      );
    case ValidationRunStatus.Running:
      return (
        <Badge variant="secondary">
          <T context="validation run outcome badge">Running</T>
        </Badge>
      );
  }
}

/** The rule types, labelled as the builder names them. */
export function useRuleTypeLabels(): Record<ValidationRuleType, string> {
  const gt = useGT();
  return {
    [ValidationRuleType.FieldConsistency]: gt('Field consistency', {
      context: 'validation rule type — fields must agree within a group',
    }),
  };
}

/** The correction strategies, labelled as the builder names them. */
export function useCorrectionStrategyLabels(): Record<
  CorrectionStrategy,
  string
> {
  const gt = useGT();
  return {
    [CorrectionStrategy.MostCommon]: gt('The value most rows carry', {
      context: 'correction strategy label',
    }),
    [CorrectionStrategy.PreferredSource]: gt('The value one division carries', {
      context: 'correction strategy label',
    }),
    [CorrectionStrategy.None]: gt('Flag it, propose nothing', {
      context: 'correction strategy label',
    }),
  };
}

/** The execution triggers, labelled as the builder names them. */
export function useTriggerLabels(): Record<ValidationRuleTrigger, string> {
  const gt = useGT();
  return {
    [ValidationRuleTrigger.Manual]: gt('Someone runs it', {
      context: 'rule trigger label',
    }),
    [ValidationRuleTrigger.Scheduled]: gt('On a schedule', {
      context: 'rule trigger label',
    }),
    [ValidationRuleTrigger.AfterIngest]: gt('After each data ingestion', {
      context: 'rule trigger label',
    }),
  };
}
